<template>
  <main class="clockspotter-login">
    <section class="brand-panel" aria-label="ClockSpotter workforce management">
      <div class="brand-panel__content">
        <img class="brand-panel__icon" src="../assets/images/logo/logo-icon.png" alt="" aria-hidden="true" />
        <p class="brand-panel__eyebrow">Workforce administration</p>
        <h1>Every workday,<br />clearly accounted for.</h1>
        <p class="brand-panel__summary">Attendance, employees, leave, and operations in one secure workspace.</p>
      </div>
      <p class="brand-panel__footer">ClockSpotter Employer Console</p>
    </section>

    <section class="login-panel">
      <div class="login-main">
        <div class="login-brand">
          <img class="login-brand__logo for-light" src="../assets/images/logo/logo.png" alt="ClockSpotter" />
          <img class="login-brand__logo for-dark" src="../assets/images/logo/logo_dark.png" alt="ClockSpotter" />
          <span>Employer Console</span>
        </div>

        <form class="theme-form" novalidate @submit.prevent="submit">
          <p class="login-kicker">Welcome back</p>
          <h2>Employer sign in</h2>
          <p class="login-intro">Use your provisioned ClockSpotter account to continue.</p>
          <div v-if="errorMessage" class="alert alert-danger" role="alert">{{ errorMessage }}</div>

          <div class="form-group">
            <label class="col-form-label" for="loginEmail">Email address</label>
            <input id="loginEmail" v-model.trim="email" class="form-control" type="email" autocomplete="username" required placeholder="name@company.com" />
            <span v-if="submitted && !validEmail" class="validate-error">Enter a valid email address.</span>
          </div>

          <div class="form-group">
            <label class="col-form-label" for="loginPassword">Password</label>
            <input id="loginPassword" v-model="password" class="form-control" type="password" autocomplete="current-password" required placeholder="Password" />
            <span v-if="submitted && !password" class="validate-error">Password is required.</span>
          </div>

          <button class="btn btn-primary w-100 login-submit" :disabled="logging" type="submit">
            <span v-if="logging" class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
            {{ logging ? 'Signing in…' : 'Sign in' }}
          </button>
        </form>
        <p class="login-support">Access is limited to authorized employer accounts.</p>
      </div>
    </section>
  </main>
</template>

<script>
import sessionService from '@/services/sessionService';
import { getErrorMessage } from '@/services/api/helpers';

export default {
  name: 'EmployerLogin',
  data() {
    return { email: '', password: '', logging: false, submitted: false, errorMessage: '' };
  },
  computed: {
    validEmail() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email);
    },
  },
  methods: {
    async submit() {
      this.submitted = true;
      this.errorMessage = '';
      if (!this.validEmail || !this.password || this.logging) return;
      this.logging = true;
      try {
        const session = await sessionService.login(this.email, this.password);
        this.$store.commit('setUserInfo', session);
        const redirect = typeof this.$route.query.redirect === 'string' && this.$route.query.redirect.startsWith('/')
          ? this.$route.query.redirect
          : '/dashboard';
        await this.$router.replace(redirect);
      } catch (error) {
        this.errorMessage = getErrorMessage(error, 'Sign in failed. Check your details and try again.');
      } finally {
        this.logging = false;
      }
    },
  },
};
</script>

<style scoped>
.clockspotter-login {
  --brand-blue: #056a9f;
  --brand-blue-dark: #034b73;
  --brand-ink: #15364d;
  display: grid;
  grid-template-columns: minmax(320px, 0.88fr) minmax(480px, 1.12fr);
  min-height: 100vh;
  background: #f5f9fc;
}

.brand-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  padding: clamp(42px, 6vw, 88px);
  color: #fff;
  background: radial-gradient(circle at 85% 18%, rgba(255, 183, 0, 0.28), transparent 22%), linear-gradient(145deg, var(--brand-blue), var(--brand-blue-dark));
}

.brand-panel::after {
  position: absolute;
  right: -110px;
  bottom: -135px;
  width: 360px;
  height: 360px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 50%;
  box-shadow: 0 0 0 54px rgba(255, 255, 255, 0.05), 0 0 0 108px rgba(255, 255, 255, 0.03);
  content: '';
}

.brand-panel__content, .brand-panel__footer { position: relative; z-index: 1; }
.brand-panel__icon { width: 52px; height: 52px; margin-bottom: clamp(58px, 10vh, 120px); padding: 8px; border-radius: 14px; background: #fff; box-shadow: 0 12px 30px rgba(0, 42, 72, 0.18); }
.brand-panel__eyebrow, .login-kicker { margin-bottom: 14px; font-size: 12px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; }
.brand-panel h1 { max-width: 560px; margin: 0 0 24px; color: #fff; font-size: clamp(36px, 4vw, 62px); font-weight: 700; line-height: 1.08; letter-spacing: -0.035em; }
.brand-panel__summary { max-width: 470px; margin: 0; color: rgba(255, 255, 255, 0.94); font-size: 17px; line-height: 1.7; }
.brand-panel__footer { margin: 56px 0 0; color: rgba(255, 255, 255, 0.9); font-size: 13px; letter-spacing: 0.05em; }

.login-panel { display: flex; align-items: center; justify-content: center; padding: 42px 24px; }
.login-main { width: min(100%, 440px); padding: 44px; border: 1px solid #e4edf3; border-radius: 20px; background: #fff; box-shadow: 0 24px 70px rgba(24, 71, 101, 0.1); }
.login-brand { display: flex; align-items: center; justify-content: space-between; margin-bottom: 44px; padding-bottom: 24px; border-bottom: 1px solid #edf2f5; }
.login-brand__logo { width: 145px; height: auto; }
.login-brand span { padding: 6px 10px; border-radius: 999px; color: #527084; background: #eef6fa; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
.login-kicker { color: var(--brand-blue); }
.theme-form h2 { margin-bottom: 10px; color: var(--brand-ink); font-size: 30px; letter-spacing: -0.02em; }
.login-intro { margin-bottom: 30px; color: #6e8492; line-height: 1.6; }
.form-group { margin-bottom: 20px; }
.col-form-label { color: #29485c; font-weight: 600; }
.form-control { min-height: 48px; border: 1px solid #dce7ed; border-radius: 10px; background: #f8fbfd; }
.form-control:focus { border-color: var(--brand-blue); background: #fff; box-shadow: 0 0 0 3px rgba(7, 139, 206, 0.12); }
.login-submit { min-height: 49px; margin-top: 8px; border-color: var(--brand-blue); border-radius: 10px; background: var(--brand-blue); font-weight: 600; }
.login-submit:hover, .login-submit:focus { border-color: var(--brand-blue-dark); background: var(--brand-blue-dark); }
.login-support { margin: 24px 0 0; color: #8a9da9; font-size: 12px; text-align: center; }

@media (max-width: 900px) {
  .clockspotter-login { grid-template-columns: 1fr; }
  .brand-panel { min-height: 265px; padding: 36px 28px; }
  .brand-panel__icon { margin-bottom: 32px; }
  .brand-panel__footer { display: none; }
  .login-panel { padding: 32px 18px; }
}

@media (max-width: 480px) {
  .brand-panel h1 { font-size: 32px; }
  .brand-panel__summary { font-size: 15px; }
  .login-main { padding: 30px 24px; }
  .login-brand span { display: none; }
}
</style>
